import React, { useEffect } from "react";
import dynamic from "next/dynamic";
import NextLink from "next/link";
import { useRouter } from "next/compat/router";
import {
  Heading,
  Text,
  Box,
  Button,
  IconFa,
  Totaliser,
  Loader,
} from "@cruk/cruk-react-components";
import { faShareAlt } from "@fortawesome/free-solid-svg-icons";
import { useInView } from "react-intersection-observer";

import { useSwrGet } from "@fwa/src/hooks/useSwrGet";
import { queryAsString } from "@fwa/src/utils/urlUtils";
import { fwsUrSuperTeamPageSlug } from "@fwa/src/services/superTeamService";
import { imagePathFromImageType } from "@fwa/src/services/imageService";
import { fwsUrlPageParentMemberships } from "@fwa/src/services/fundraisingPageService";
import { useBrandContext } from "@fwa/src/contexts/BrandContext";
import { useTracking } from "@fwa/src/hooks/useTracking";
import { sendGTMTrackingEvent } from "@fwa/src/services/gtm";

import { ErrorPageWrapper } from "@fwa/src/components/ErrorPageWrapper";
import { MemberOf } from "@fwa/src/components/MemberOf";
import { Share } from "@fwa/src/components/Share";
import { SuperTeamFundraisingPagesOverview } from "@fwa/src/components/SuperTeamFundraisingPagesOverview";
import { SuperTeamTeamsOverview } from "@fwa/src/components/SuperTeamTeamsOverview";
import { TotalsSection } from "@fwa/src/components/TotalsSection";
import { Seasonal } from "@fwa/src/components/Seasonal";

import sharedStyles from "@fwa/src/components/styles.module.css";

import type {
  SuperTeamPageType,
  BrandNameType,
  FwsResponseData,
  TeamMemberType,
} from "@fwa/src/types";
import { isPast, isToday } from "@fwa/src/utils/timeUtils";

const FeedListFilter = dynamic(
  () => import("@fwa/src/components/FeedListFilter"),
  { ssr: false },
);

export function SuperTeamPage({
  pageData,
  themeName,
}: {
  pageData: SuperTeamPageType;
  themeName?: string;
}) {
  //  figure out unique page url for page cacheId
  const { trackEventGtm } = useTracking();
  const [brandName, setBrandName] = useBrandContext();
  const router = useRouter();
  const { slug } = router?.query || {};
  const slugString = queryAsString(slug);
  const url = slugString.length
    ? `${fwsUrSuperTeamPageSlug({ slug: slugString })}`
    : null;

  const { ref: feedRef, inView: feedInView } = useInView({
    triggerOnce: true,
    rootMargin: "200px",
  });

  // page cache
  const opts = { fallbackData: pageData };
  const { data: page, error } = useSwrGet<SuperTeamPageType>(url, opts);
  const hasTarget = page?.target && page?.target > 0;
  const pageCloseDate = page?.closeDate ? new Date(page.closeDate) : null;
  const isPageClosed: boolean =
    page?.pageStatus === "closed" ||
    (pageCloseDate && (isToday(pageCloseDate) || isPast(pageCloseDate))) ||
    false;

  const memberShipsUrl = page
    ? `${fwsUrlPageParentMemberships({
        pageId: page.uniqueId || "",
        currentPage: 1,
        itemsPerPage: 3,
      })}`
    : null;

  const { data: membershipsData } =
    useSwrGet<FwsResponseData<TeamMemberType>>(memberShipsUrl);
  const memberships = membershipsData?.results || [];

  useEffect(() => {
    if (!!themeName && themeName !== brandName) {
      setBrandName(themeName as BrandNameType);
    }
  }, [themeName, brandName, setBrandName]);

  useEffect(() => {
    sendGTMTrackingEvent({
      event: "pageView",
      pagePath: "/superteam-page",
      pageTitle: "super team page",
    });
  }, []);

  return (
    <ErrorPageWrapper error={error!}>
      <div className={sharedStyles["content-max-width"]}>
        {/* Loading state */}
        {!page ? <Loader /> : null}

        {/* Data state */}
        {!!page && (
          <>
            {/* Top actions block */}
            <Box
              backgroundColor="background-light"
              padding="xs"
              marginBottom="none"
            >
              <div
                className={sharedStyles["row-stretch"]}
                data-component="page-actions-top"
              >
                <Share
                  page={page}
                  feedItem={null}
                  isOwner={false}
                  popoverPosition="bottom"
                  ctaLocation="header"
                >
                  <Button
                    type="button"
                    appearance="secondary"
                    data-cta-type="open-share"
                  >
                    <IconFa faIcon={faShareAlt} />
                    Share
                  </Button>
                </Share>
                {isPageClosed ? null : (
                  <NextLink
                    href={`/donate/${page.uniqueId}/details`}
                    onClick={() => {
                      trackEventGtm({
                        event: "donate_cta",
                        cta: "header",
                      });
                    }}
                    data-cta-type="donate"
                  >
                    <Button appearance="primary" as="span">
                      Donate
                    </Button>
                  </NextLink>
                )}
              </div>
            </Box>
            <Box margin="none" data-component="super-team-header">
              <Seasonal>
                <img
                  className={sharedStyles["header-image"]}
                  src={
                    page.headerImage
                      ? imagePathFromImageType(page.headerImage)
                      : ""
                  }
                  alt=""
                  width="1200px"
                  height="600px"
                />
              </Seasonal>
            </Box>

            {/* Title block */}
            <Box
              backgroundColor="primary"
              padding="s"
              data-component="super-team-title"
            >
              <Heading
                h1
                textColor="text-on-primary"
                margin="none"
                overflowWrap="break-word"
                style={{ display: "inline-block" }}
              >
                {page.title}
              </Heading>
            </Box>
            {/* totaliser block */}
            <Box
              backgroundColor="background-light"
              data-component="super-team-totaliser"
              padding="s"
            >
              <div className={sharedStyles["block-center"]}>
                <div style={{ maxWidth: "560px" }}>
                  <Totaliser
                    isCompact={false}
                    target={hasTarget ? page.target : undefined}
                    total={page.donationsTotalAmount}
                    giftAid={page.donationsGiftAidTotalAmount}
                  />
                </div>
              </div>
            </Box>

            {/* subtitle block */}
            {(page.subtitle || !!memberships.length) && (
              <Box backgroundColor="primary" padding="s">
                {page.subtitle ? (
                  <Heading
                    h2
                    textColor="text-on-primary"
                    marginBottom="none"
                    paddingBottom="none"
                    data-component="super-team-subtitle"
                  >
                    {page.subtitle}
                  </Heading>
                ) : null}
                {!!memberships.length && membershipsData && (
                  <Box marginTop={page.subtitle ? "xs" : "none"}>
                    <MemberOf
                      page={page}
                      textOnPrimary={true}
                      fallbackData={membershipsData}
                    />
                  </Box>
                )}
              </Box>
            )}

            {/* members block */}
            <SuperTeamTeamsOverview page={page} />

            <SuperTeamFundraisingPagesOverview page={page} />

            {/* story block */}
            {page.story ? (
              <Box backgroundColor="primary" padding="s">
                <Heading h2 textColor="text-on-primary">
                  Our story
                </Heading>
                <Text
                  textColor="text-on-primary"
                  style={{
                    width: "100%",
                    display: "inline-block",
                    whiteSpace: "pre-line",
                  }}
                >
                  {page.story}
                </Text>
              </Box>
            ) : null}

            {/* Feeditems block */}
            <div ref={feedRef}>
              <Box
                backgroundColor="background-light"
                padding="s"
                marginBottom="xl"
              >
                {page.donationsCount || page.postsCount ? (
                  <Box>
                    <Heading h2>Latest updates</Heading>
                    {feedInView && (
                      <FeedListFilter
                        canCreate={false}
                        pageId={page.uniqueId}
                        feedItemTotals={{
                          feeditems: page.donationsCount + page.postsCount,
                          donations: page.donationsCount,
                          posts: page.postsCount,
                          activitys: 0,
                        }}
                        pageUrl={url}
                        showParentPageLink
                      />
                    )}
                  </Box>
                ) : null}
                <Box>
                  <TotalsSection
                    donationsTotalAmount={page.donationsTotalAmount}
                    donationsTotalAmountFake={page.donationsTotalAmountFake}
                    donationsTotalAmountFacebook={
                      page.donationsTotalAmountFacebook
                    }
                  />
                </Box>
              </Box>
            </div>
          </>
        )}
      </div>
    </ErrorPageWrapper>
  );
}

export default SuperTeamPage;
